import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ResolveViolationsCardsList from './components/ResolveViolationsCardsList';
import ResolutionConfirmation from './components/ResolutionConfirmation';
import Spinner from '../../components/Spinner';
import {
	fetchItems,
	submitResolution,
	resetState,
} from '../../../data/reducers/resolveViolationsReducer';
import './ResolveViolationsPage.css';

/**
 * Bulk violation resolution page component.
 *
 * Allows users to:
 * - View items from daily digest emails
 * - Select multiple items for resolution
 * - Submit bulk resolution requests to admins
 *
 * URL Parameters:
 * - user_id: User ID
 * - email_ids: Comma-separated queued email IDs
 */
const ResolveViolationsPage = () => {
	const dispatch = useDispatch();
	const history = useHistory();

	// Get URL parameters
	const urlParams = new URLSearchParams(window.location.search);
	const userId = urlParams.get('user_id');
	const emailIds = urlParams.get('email_ids');

	const {
		items,
		selectedItems,
		isLoading,
		isSubmitting,
		error,
		submitSuccess,
	} = useSelector((state) => state.resolveViolations);

	const [showConfirmation, setShowConfirmation] = useState(false);

	useEffect(() => {
		// Reset state on mount
		dispatch(resetState());

		// Validate parameters
		if (!userId || !emailIds) {
			return;
		}

		// Fetch items
		dispatch(fetchItems(userId, emailIds));
	}, [userId, emailIds, dispatch]);

	/**
	 * Handle submission of selected items.
	 */
	const handleSubmit = () => {
		if (selectedItems.length === 0) {
			alert('Please select at least one item to resolve.');
			return;
		}

		// Prepare items for submission
		const itemsToSubmit = items
			.filter((item) => selectedItems.includes(item.queued_email_id))
			.map((item) => ({
				queued_email_id: item.queued_email_id,
				dataset: item.dataset_type,
				item_id: item.item_id,
				property_id: item.property_id,
			}));

		dispatch(submitResolution(userId, itemsToSubmit));
	};

	// Show confirmation page on success
	useEffect(() => {
		if (submitSuccess) {
			setShowConfirmation(true);
		}
	}, [submitSuccess]);

	const navigate = useCallback(
		(path) => {
			history.push(path);
		},
		[history],
	);

	if (showConfirmation && submitSuccess) {
		return <ResolutionConfirmation itemCount={selectedItems.length} />;
	}

	return (
		<div className="resolve-violations-page">
			<div className="resolve-violations-container">
				<header className="resolve-violations-header">
					<h1>Resolve Violations</h1>
					<p>Select the items you'd like to request resolution for</p>
				</header>

				{isLoading && (
					<div className="resolve-violations-loading">
						<Spinner />
						<p>Loading items...</p>
					</div>
				)}

				{error && (
					<div className="resolve-violations-error">
						<h3>Error</h3>
						<p>{error}</p>
						<button
							onClick={() => navigate('/dashboard')}
							className="btn btn-primary"
						>
							Go to Dashboard
						</button>
					</div>
				)}

				{!isLoading && !error && items.length === 0 && !userId && !emailIds && (
					<div className="resolve-violations-error">
						<h3>Invalid Request</h3>
						<p>Missing required parameters: user_id or email_ids</p>
						<button
							onClick={() => navigate('/dashboard')}
							className="btn btn-primary"
						>
							Go to Dashboard
						</button>
					</div>
				)}

				{!isLoading && !error && items.length === 0 && userId && emailIds && (
					<div className="resolve-violations-empty">
						<h3>No Items Found</h3>
						<p>There are no items available for resolution.</p>
						<button
							onClick={() => navigate('/dashboard')}
							className="btn btn-primary"
						>
							Go to Dashboard
						</button>
					</div>
				)}

				{!isLoading && !error && items.length > 0 && (
					<>
						<ResolveViolationsCardsList
							items={items}
							selectedItems={selectedItems}
						/>

						<div className="resolve-violations-footer">
							<div className="selection-summary">
								<strong>{selectedItems.length}</strong> item(s) selected
							</div>
							<div className="action-buttons">
								<button
									className="btn btn-secondary"
									onClick={() => navigate('/dashboard')}
									disabled={isSubmitting}
								>
									Cancel
								</button>
								<button
									className="btn btn-primary"
									onClick={handleSubmit}
									disabled={selectedItems.length === 0 || isSubmitting}
								>
									{isSubmitting
										? 'Submitting...'
										: `Resolve ${selectedItems.length} Item(s)`}
								</button>
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default ResolveViolationsPage;
